import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { fetchSettledContract } from "../../../common/redux/thunks/contract/load-contract-thunk";
import AwsS3, { AwsS3MultipartOptions } from "@uppy/aws-s3";
import Uppy, { UppyFile } from "@uppy/core";
import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";
import ProgressBar from "@uppy/progress-bar";
import "@uppy/progress-bar/dist/style.css";
import { DragDrop } from "@uppy/react";
import { AskFurtherButton } from "../../../common/agent/AskFurtherButton";
import { renderedText } from "../../../common/agent/renderedText";
import { t } from "i18next";
import { isFinite } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCheckCircle, FaExclamationCircle, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import {
  DocumentChatPurpose,
  ContractDto,
  ContractTemplateDto,
  GPTRole,
  ProjectDto,
  bffApi,
  useCreateContractMutation,
  useCreateUploadForContractMutation,
  useDeleteContractDraftMutation,
  useStartDocumentChatMutation,
  useUpdateContractMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { getPreferredLanguage } from "../../../common/local-storage/language";
import { useOrganizationSearch } from "../../../common/organization/useOrganizationSearch";
import { getPusher } from "../../../common/pusher";
import { useAppDispatch } from "../../../common/redux/hooks";
import { urls } from "../../../urls";
import {
  ACCEPTED_FILE_TYPES,
  fileTypeProblem,
  parseAnalysis,
  saveThenReload,
  unreadablePagesNotice,
} from "./aiContractDocument";
import { organizationHasSeveralDepartments } from "../../../common/departments/departmentVisibility";
import {
  contractDepartmentOptions,
  initialContractDepartmentId,
} from "../../../common/departments/eventCreationDepartments";

enum CreateContractWithAIState {
  Idle,
  IsInitializingContract,
  ContractInitialized,
  UploadCreated,
  UploadScanned,
  DocumentCreated,
  IsAnalyzingDocument,
  DocumentAnalyzed,
  ContractCreated,
}

const createPrompt = (templates: ContractTemplateDto[], loggedInOrg: string) => `
  Based on the given document text, populate the values in the following json:

  {
    "title": null, // Should be short but descriptive
    "description": null, // Should be short but descriptive. 1-2 paragraphs.
    "templateId": null, // Should be one of the UUIDs in the list below. Select the most appropriate template. Should be null if no template fits. Must be a valid UUID or null!!!
    "startDate": null, // Should be formatted as yyyy-MM-dd. Should be null if there is no start date.
    "endDate": null, // Should be formatted as yyyy-MM-dd. Should be null if there is no end date.
    "paymentTerms": number; // Should be the payment terms in number of days. Should be null or an integer.
    "noticePeriod": number; // Should be the notice period in number of months. Should be null or an integer.
    "counterparty": {
      companyName: string;
      fullName?: string;
      email?: string;
    },
  }

  The current user is logged in on behalf of ${loggedInOrg}

  The templateId should be one of the UUIDs in the following list:
  ${templates.map((t) => `${JSON.stringify({ uuid: t.id, name: t.name, description: t.description })}, `)}

  The title and description should in the language of the logged in user: ${getPreferredLanguage()}

  It is very important that you return a plain, valid json of the provided format only.
`;

export const CreateContractWithAIForm = ({
  templates,
  project,
}: {
  templates: ContractTemplateDto[];
  project?: ProjectDto;
}) => {
  const authState = useLoggedInWithOrgContextState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const errorDisplayer = useApiError();

  const [createContractState, setCreateContractState] = useState<CreateContractWithAIState>(
    CreateContractWithAIState.Idle
  );
  const [createContract] = useCreateContractMutation();
  const [createUpload] = useCreateUploadForContractMutation();
  const [analyzeDocument, { error: analyzeDocumentError }] = useStartDocumentChatMutation();
  const [updateContract] = useUpdateContractMutation();
  const [deleteContractDraft] = useDeleteContractDraftMutation();
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [unreadablePages, setUnreadablePages] = useState<number[]>([]);
  const [analysisFailure, setAnalysisFailure] = useState<string>();

  // TODO: Avoid listing all orgs
  const { organizationCombinations } = useOrganizationSearch({ limit: 10_000 });

  const departments = contractDepartmentOptions(authState.selectedOrg);
  const showsPicker = organizationHasSeveralDepartments(authState.selectedOrg);
  const [departmentId, setDepartmentId] = useState(() => initialContractDepartmentId(authState.selectedOrg));
  // Rebuilding the Uppy instance drops its in-flight uploads, so the department is read through a ref.
  const departmentIdRef = useRef(departmentId);
  departmentIdRef.current = departmentId;

  const [target, setTarget] = useState<string>();
  const [pagesAnalyzed, setPagesAnalyzed] = useState<number>();
  const [contract, setContract] = useState<ContractDto>();
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTarget("#dnd");
  }, []);

  const analyzeDocumentAndFillContract = useCallback(
    (contractId: string, documentId: string) => {
      setCreateContractState(CreateContractWithAIState.IsAnalyzingDocument);
      analyzeDocument({
        startDocumentChatRequest: {
          documentId,
          purpose: DocumentChatPurpose.Creating,
          messages: [
            {
              role: GPTRole.System,
              content: createPrompt(templates, authState.selectedOrg.name),
            },
          ],
          reasoningEffort: "none",
        },
      })
        .unwrap()
        .then(async (res) => {
          const data = parseAnalysis(res.messages[res.messages.length - 1].content);
          setCreateContractState(CreateContractWithAIState.DocumentAnalyzed);
          setUnreadablePages(res.unreadablePages ?? []);
          setPagesAnalyzed(res.pageCount ?? undefined);
          const counterparty = organizationCombinations
            ?.filter((o) => o.id !== authState.selectedOrg.id)
            ?.find((o) => o.name.toLowerCase() === data.counterparty?.companyName?.toLowerCase());
          setContract((c) =>
            c
              ? {
                  ...c,
                  title: data.title,
                  description: data.description,
                  template: templates.find((t) => t.id === data.templateId),
                  counterparty: {
                    organizationEntry: {
                      id: "",
                      name: data.counterparty.companyName,
                      country: {
                        id: "",
                        alpha3Code: "",
                        name: "",
                      },
                      industries: [],
                      organizationNumber: "",
                    },
                  },
                  dataFields: {
                    startDate: data.startDate,
                    endDate: {
                      date: data.endDate,
                      hasNoEndDate: false,
                    },
                    counterpartyContactPerson: {
                      fullName: data.counterparty.fullName,
                      email: data.counterparty.email,
                    },
                    paymentTermsInDays: data.paymentTerms,
                    noticePeriod: data.noticePeriod,
                    addedDataFields: [],
                    requiredDataFields: [],
                    missingDataFields: [],
                  },
                }
              : undefined
          );
          const save = () => updateContract({
            contractId,
            updateContractRequest: {
              title: data.title,
              description: data.description,
              template: {
                templateId: data.templateId,
              },
              startDate: data.startDate
                ? {
                    date: data.startDate,
                  }
                : undefined,
              endDate: data.endDate
                ? {
                    date: data.endDate,
                    hasNoEndDate: false,
                  }
                : undefined,
              counterpartyContactPerson: data.counterparty
                ? {
                    fullName: data.counterparty.fullName,
                    email: data.counterparty.email,
                  }
                : undefined,
              paymentTermsInDays: isFinite(data.paymentTerms)
                ? {
                    days: data.paymentTerms,
                  }
                : undefined,
              editNoticePeriod: data.noticePeriod
                ? {
                    duration: data.noticePeriod,
                  }
                : undefined,
              counterparty: counterparty
                ? {
                    counterparty: {
                      organizationEntryId: counterparty?.type === "OrganizationEntry" ? counterparty.id : undefined,
                      organizationId: counterparty?.type === "Organization" ? counterparty.id : undefined,
                    },
                  }
                : undefined,
            },
          })
            .unwrap()
            .catch((e) => {
              errorDisplayer.trigger(e);
            });
          const reload = () => dispatch(fetchSettledContract({ contractId })).unwrap();
          await saveThenReload(save, reload);
          setCreateContractState(CreateContractWithAIState.ContractCreated);
        })
        .catch((e) => {
          setAnalysisFailure(errorDisplayer.message(e));
          setCreateContractState(CreateContractWithAIState.Idle);
          deleteContractDraft({ contractId })
            .unwrap()
            .catch(() => undefined);
          setContract(undefined);
        });
    },
    [
      analyzeDocument,
      deleteContractDraft,
      dispatch,
      authState.selectedOrg.id,
      authState.selectedOrg.name,
      errorDisplayer,
      organizationCombinations,
      templates,
      updateContract,
    ]
  );

  const documentAddedHandler = useCallback(async () => {
    if (!contract?.id) return;
    setCreateContractState(CreateContractWithAIState.UploadScanned);
    const request = dispatch(bffApi.endpoints.getContract.initiate({ contractId: contract.id }));
    const contractWithDocument = await request.unwrap().finally(() => request.unsubscribe());
    const document = contractWithDocument.documentFields?.documents[0];
    if (document) {
      setContract(contractWithDocument);
      setCreateContractState(CreateContractWithAIState.DocumentCreated);
      analyzeDocumentAndFillContract(contractWithDocument.id, document.id);
    } else {
      const e = Error("Illegal state - no documents when handling added document");
      errorDisplayer.trigger(e);
      throw e;
    }
  }, [contract?.id, dispatch, errorDisplayer, analyzeDocumentAndFillContract]);

  useEffect(() => {
    const channel = contract?.id ? getPusher().subscribe(contract.id) : undefined;
    channel?.bind("document-added", documentAddedHandler);

    return () => {
      channel?.unbind("document-added", documentAddedHandler);
    };
  }, [documentAddedHandler, contract?.id]);

  const { uppy } = useMemo(() => {
    const uppy = new Uppy({
      id: "AwsS3",
      autoProceed: true,
      restrictions: { allowedFileTypes: ACCEPTED_FILE_TYPES },
      onBeforeFileAdded(currentFile) {
        const problem = fileTypeProblem(currentFile.name ?? "", t);
        setFileProblem(problem);
        setAnalysisFailure(undefined);
        if (problem) return false;
        currentFile["id"] = uuid();
        return currentFile;
      },
    })
      .use(ProgressBar, { target })
      .use(AwsS3, {
        id: "AwsS3",
        limit: 1,
        async getUploadParameters(file: UppyFile<Record<string, unknown>, Record<string, unknown>>) {
          if (!isFile(file.data)) throw Error("Only files, not blobs, are supported.");
          const departmentId = departmentIdRef.current;
          if (!departmentId) throw Error("No department selected for the contract.");
          setCreateContractState(CreateContractWithAIState.IsInitializingContract);
          const initialContract = await createContract({
            depId: departmentId,
            createContractRequest: {
              title: "",
              timezone: moment.tz.guess(),
              projects: project ? [project.id] : undefined,
            },
          })
            .unwrap()
            .catch((e) => {
              errorDisplayer.trigger(e);
              throw e;
            });
          setCreateContractState(CreateContractWithAIState.ContractInitialized);
          setContract(initialContract);
          const upload = await createUpload({
            contractId: initialContract.id,
            uploadId: file.id,
            createUploadRequestDto: {
              fileName: file.name ?? "",
              fileSize: file.size ?? 0,
              fileExtension: file.extension,
              mimeType: file.data.type,
            },
          })
            .unwrap()
            .catch((e) => {
              errorDisplayer.trigger(e);
              throw e;
            });
          setContract({
            ...initialContract,
            editingMetaFields: {
              uploads: [upload],
              descriptionUploads: [],
            },
          });
          setCreateContractState(CreateContractWithAIState.UploadCreated);
          return {
            url: upload.url,
            method: "POST",
            fields: {
              "x-amz-date": upload.renamedXAmzDate,
              "x-amz-signature": upload.renamedXAmzSignature,
              "x-amz-algorithm": upload.renamedXAmzAlgorithm,
              "x-amz-credential": upload.renamedXAmzCredential,
              policy: upload.policy,
              key: upload.key,
            },
          };
        },
      } as unknown as AwsS3MultipartOptions<Record<string, unknown>, Record<string, unknown>>);

    uppy.on("restriction-failed", (file) => setFileProblem(fileTypeProblem(file?.name ?? "", t)));

    return { uppy };
  }, [createContract, createUpload, errorDisplayer, project, target]);

  useEffect(() => {
    if (analysisFailure) uppy.cancelAll();
  }, [analysisFailure, uppy]);

  return (
    <Flex flexDirection={"column"}>
      {showsPicker && (
        <FormControl pt="4" display={createContractState === CreateContractWithAIState.Idle ? "block" : "none"}>
          <FormLabel>{t("Department")}</FormLabel>
          <Select
            value={departmentId ?? ""}
            placeholder={t("Select department") ?? undefined}
            onChange={(e) => setDepartmentId(e.target.value || undefined)}
          >
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </Select>
          <FormHelperText>{t("The contract belongs to this department")}</FormHelperText>
        </FormControl>
      )}
      <FormControl
        pt="4"
        isDisabled={!departmentId}
        isInvalid={fileProblem != null || analysisFailure != null}
        display={createContractState === CreateContractWithAIState.Idle && departmentId ? "block" : "none"}
      >
        <FormLabel>{t("Add contract document")}</FormLabel>
        <div id="dnd">
          <DragDrop width="100%" uppy={uppy} />
        </div>
        <FormErrorMessage>
          {fileProblem ?? `${t("Neural net analysis failed")}. ${analysisFailure}`}
        </FormErrorMessage>
      </FormControl>
      {createContractState === CreateContractWithAIState.IsInitializingContract && (
        <Flex alignItems={"center"} pt="4">
          <Spinner size="xs" thickness="2px" color="blue.200" mr="2" />
          <Text fontSize={"sm"} color="smMuted">
            {t("Creating contract")}...
          </Text>
        </Flex>
      )}
      {createContractState >= CreateContractWithAIState.ContractInitialized && (
        <Flex alignItems={"center"} pt="4">
          <Icon as={FaCheckCircle} color={"green.400"} mr="2" />
          <Text fontSize={"sm"} color="smMuted">
            {t("Contract created")}
          </Text>
        </Flex>
      )}
      {createContractState === CreateContractWithAIState.UploadCreated && (
        <Flex alignItems={"center"}>
          <Spinner size="xs" thickness="2px" color="blue.200" mr="2" />
          <Text fontSize={"sm"} color="smMuted">
            {t("Scanning document")}...
          </Text>
        </Flex>
      )}
      {createContractState >= CreateContractWithAIState.UploadScanned && (
        <Flex alignItems={"center"}>
          <Icon as={FaCheckCircle} color={"green.400"} mr="2" />
          <Text fontSize={"sm"} color="smMuted">
            {t("Document virus scan complete")}
          </Text>
        </Flex>
      )}
      {createContractState === CreateContractWithAIState.IsAnalyzingDocument && (
        <Flex alignItems={"center"}>
          {analyzeDocumentError ? (
            <>
              <Icon as={FaTimesCircle} color="red.400" mr="2" />
              <Text fontSize={"sm"} color="red.400">
                {t("Neural net analysis failed")}...
              </Text>
            </>
          ) : (
            <>
              <Spinner size="xs" thickness="2px" color="blue.200" mr="2" />
              <Text fontSize={"sm"} color="smMuted">
                {t("Running deep neural net analysis")}...
              </Text>
            </>
          )}
        </Flex>
      )}
      {createContractState >= CreateContractWithAIState.DocumentAnalyzed ? (
        <Flex flexDirection="column" pb="4">
          <Flex alignItems="center">
            <Icon as={FaCheckCircle} color={"green.400"} mr="2" />
            <Text fontSize={"sm"} color="smMuted">
              {t("Deep neural net analysis complete")}.
              {pagesAnalyzed ? ` ${t("Analyzed")} ${pagesAnalyzed} ${t("pages")}.` : ""}
            </Text>
          </Flex>
          {unreadablePagesNotice(unreadablePages, t) ? (
            <Flex alignItems="center">
              <Icon as={FaExclamationCircle} color="orange.400" mr="2" />
              <Text fontSize={"sm"} color="smMuted">
                {unreadablePagesNotice(unreadablePages, t)}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      ) : null}
      {createContractState === CreateContractWithAIState.ContractCreated && contract ? (
        <Flex ref={resultRef} flexDirection={"column"} overflow="auto" backgroundColor={"smBackgroundSecondary"} p="4">
          {contract.title && (
            <Text fontWeight={"bold"} pt="4">
              {contract.title}
            </Text>
          )}
          {contract.description && (
            <Text fontSize={"sm"} py="2">
              {contract.description}
            </Text>
          )}
          {contract.template && (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("Contract type")}</Text>
              <Text fontWeight={"bold"}>{contract.template.name}</Text>
            </Flex>
          )}
          {contract.dataFields?.startDate && (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("Start date")}</Text>
              <Text fontWeight={"bold"}>{contract.dataFields.startDate}</Text>
            </Flex>
          )}
          {contract.dataFields?.endDate?.date && (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("End date")}</Text>
              <Text fontWeight={"bold"}>{contract.dataFields.endDate.date}</Text>
            </Flex>
          )}
          {contract.dataFields?.paymentTermsInDays ? (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("Payment terms")}</Text>
              <Text fontWeight={"bold"}>
                {contract.dataFields.paymentTermsInDays} {t("days")}
              </Text>
            </Flex>
          ) : null}
          {contract.dataFields?.noticePeriod && (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("Notice period")}</Text>
              <Text fontWeight={"bold"}>
                {contract.dataFields.noticePeriod} {t("months")}
              </Text>
            </Flex>
          )}
          {contract.dataFields?.counterpartyContactPerson && (
            <Flex flexDirection={"column"} fontSize={"sm"} py="2">
              <Text fontSize={"sm"}>{t("Counterparty")}</Text>
              <Text fontWeight={"bold"} pb="2">
                {contract.counterparty?.organizationEntry?.name}
                {contract.counterparty?.organizationEntry?.organizationNumber}
              </Text>
              <Text>{contract.dataFields.counterpartyContactPerson.fullName}</Text>
              <Text>{contract.dataFields.counterpartyContactPerson.email}</Text>
            </Flex>
          )}
        </Flex>
      ) : null}
      <Flex justifyContent={"end"} alignItems={"end"} pt="4" gap="2">
        {createContractState === CreateContractWithAIState.ContractCreated && contract ? (
          <AskFurtherButton
            size="md"
            result={() => ({
              source: t("Contract read by AI"),
              subject: contract.title || undefined,
              text: renderedText(resultRef.current),
            })}
            beforeOpen={() => navigate(urls.contracts.edit.go(contract.id).details, { replace: true })}
          />
        ) : null}
        <Button
          colorScheme="teal"
          isDisabled={createContractState !== CreateContractWithAIState.ContractCreated}
          onClick={() => {
            if (!contract) return;
            if (urls.contracts.edit.isCurrentPage()) {
              window.location.replace(urls.contracts.edit.go(contract.id).details.fullPathName("details"));
            } else navigate(urls.contracts.edit.go(contract.id).details, { replace: true });
          }}
        >
          {t("Next")}
        </Button>
      </Flex>
    </Flex>
  );
};

const isFile = (file: File | Blob): file is File => {
  return (file as File).lastModified !== undefined;
};
